type StreamEvent = 'data' | 'end' | 'error'
type StreamListener = (value?: string | Error) => void

// Papa's readable-stream input accepts decoded strings and keeps CSV records
// across chunks. This adapter uses browser APIs and retains one UTF-8 decoder
// for the entire file, including any incomplete bytes at each read boundary.
export function createCsvTextStream(file: File, chunkSize: number, encoding = 'utf-8') {
    const decoder = new TextDecoder(encoding, { fatal: true })
    const listeners = new Map<StreamEvent, Set<StreamListener>>()
    let cancelled = false

    const emit = (event: StreamEvent, value?: string | Error) => {
        listeners.get(event)?.forEach((listener) => listener(value))
    }

    const stream = {
        readable: true,
        read: () => null,
        on(event: StreamEvent, listener: StreamListener) {
            let handlers = listeners.get(event)
            if (!handlers) {
                handlers = new Set()
                listeners.set(event, handlers)
            }
            handlers.add(listener)
            return stream
        },
        removeListener(event: StreamEvent, listener: StreamListener) {
            listeners.get(event)?.delete(listener)
            return stream
        },
        cancel() {
            cancelled = true
            listeners.clear()
        },
        async start() {
            try {
                for (let offset = 0; offset < file.size && !cancelled; offset += chunkSize) {
                    const bytes = await file.slice(offset, Math.min(offset + chunkSize, file.size)).arrayBuffer()
                    if (cancelled) return
                    const text = decoder.decode(bytes, { stream: true })
                    if (text) emit('data', text)
                }
                if (cancelled) return
                const tail = decoder.decode()
                if (tail) emit('data', tail)
                if (!cancelled) emit('end')
            } catch (error) {
                emit('error', error instanceof Error ? error : new Error(String(error)))
            }
        },
    }

    return stream
}
